import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { compose } from 'recompose';

import {
  CoachInfo as CoachInfoModel,
  PRICING_PACKAGES,
  FieldLabels,
} from '../../../../../Model/CoachInfo';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';

import { SaveButton } from '../../../../../components/Button/ActionButtons';
import useToast from '../../../../hooks/useToast';

import CoachInfoFormField from '../CoachInfoFormField';
import PricingTable from '../PricingTable';
import {
  isAnyFieldFilled,
  isAnyPricingFieldFilled,
  removeEmptyFields,
  trimArrayWithObjectValues,
  trimObjectValues,
} from './utils';
import { FormSection } from './styles';
import texts from './texts';

const CoachInfoForm = ({ coachId, setIsLoading }) => {
  const [coachInfoDoc, setCoachInfoDoc] = useState(null);
  const [basicInfo, setBasicInfo] = useState({});
  const [pricing, setPricing] = useState([]);
  const [serviceInfo, setServiceInfo] = useState({});
  const [professionalInfo, setProfessionalInfo] = useState({});
  const [contactInfo, setContactInfo] = useState({});

  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const { userId } = useContext(LoggedInUserContext);

  const resetFields = useCallback(() => {
    setBasicInfo({
      coachName: '',
      coachNamePronunciation: '',
      coachLocation: '',
      coachGender: '',
    });
    setPricing(Object.values(PRICING_PACKAGES).map((month) => ({ month, price: '', comments: '' })));
    setServiceInfo({
      specificRequirements: '',
      trainingProgramme: '',
      nutritionPlan: '',
      pointOfContact: '',
      responseTime: '',
    });
    setProfessionalInfo({
      valuesAndBeliefs: '',
      educationAndQualification: '',
      clientRequirementsAccepts: '',
      clientRequirementsDoesNotAccept: '',
      personas: '',
    });
    setContactInfo({
      contactEmail: '',
      websiteURL: '',
      instagram: '',
      tiktok: '',
      youtube: '',
    });
  }, []);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      setCoachInfoDoc(null);
      resetFields();

      if (coachId) {
        const coachInfoDocuments = await CoachInfoModel.getCoachInfosByCoachId(coachId);
        const currentCoachInfoDoc = coachInfoDocuments?.docs?.find((doc) => doc.current);
        if (isComponentMountedRef.current && !!currentCoachInfoDoc) {
          setCoachInfoDoc(currentCoachInfoDoc);
          setBasicInfo({
            coachName: currentCoachInfoDoc.coachName,
            coachNamePronunciation: currentCoachInfoDoc.coachNamePronunciation,
            coachLocation: currentCoachInfoDoc.coachLocation,
            coachGender: currentCoachInfoDoc.coachGender,
          });
          setPricing(currentCoachInfoDoc.pricing?.length > 0
            ? currentCoachInfoDoc.pricing : Object.values(PRICING_PACKAGES)
              .map((month) => ({ month, price: '', comments: '' })));
          setServiceInfo({
            specificRequirements: currentCoachInfoDoc.specificRequirements,
            trainingProgramme: currentCoachInfoDoc.trainingProgramme,
            nutritionPlan: currentCoachInfoDoc.nutritionPlan,
            pointOfContact: currentCoachInfoDoc.pointOfContact,
            responseTime: currentCoachInfoDoc.responseTime,
          });
          setProfessionalInfo({
            valuesAndBeliefs: currentCoachInfoDoc.valuesAndBeliefs,
            educationAndQualification: currentCoachInfoDoc.educationAndQualification,
            clientRequirementsAccepts: currentCoachInfoDoc.clientRequirementsAccepts,
            clientRequirementsDoesNotAccept: currentCoachInfoDoc.clientRequirementsDoesNotAccept,
            personas: currentCoachInfoDoc.personas,
          });
          setContactInfo({
            contactEmail: currentCoachInfoDoc.contactEmail,
            websiteURL: currentCoachInfoDoc.websiteURL,
            instagram: currentCoachInfoDoc.instagram,
            tiktok: currentCoachInfoDoc.tiktok,
            youtube: currentCoachInfoDoc.youtube,
          });
        }
      }
      if (isComponentMountedRef.current) {
        setIsLoading(false);
      }
    };
    init();
  }, [
    coachId,
    isComponentMountedRef,
    resetFields,
    setIsLoading,
  ]);

  const handlePricingChange = useCallback((index, field, value) => {
    setPricing((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  }, []);

  const onSave = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = {
        coachId,
        current: true,
        ...removeEmptyFields({
          ...trimObjectValues(basicInfo),
          ...trimObjectValues(serviceInfo),
          ...trimObjectValues(professionalInfo),
          ...trimObjectValues(contactInfo),
        }),
        pricing: trimArrayWithObjectValues(pricing),
        updatedAt: new Date(),
        updatedBy: userId,
      };

      if (coachInfoDoc && coachInfoDoc.id) {
        await coachInfoDoc.updateCoachInfos(data);
      } else {
        await CoachInfoModel.createCoachInfo(coachId, data);
      }

      showToast(texts.savedSuccessfully, { type: 'success' });
    } catch (error) {
      showToast(texts.errorSavingInfo, { error: true });
    } finally {
      setIsLoading(false);
    }
  }, [
    coachId,
    basicInfo,
    pricing,
    serviceInfo,
    professionalInfo,
    contactInfo,
    coachInfoDoc,
    showToast,
    userId,
    setIsLoading,
  ]);

  const isSomeFieldFilled = useMemo(() => isAnyFieldFilled(basicInfo)
        || isAnyFieldFilled(serviceInfo)
        || isAnyFieldFilled(professionalInfo)
        || isAnyFieldFilled(contactInfo)
        || isAnyPricingFieldFilled(pricing),
  [basicInfo, serviceInfo, professionalInfo, contactInfo, pricing]);

  return (
    <>
      <FormSection>
        {[FieldLabels.CoachName, FieldLabels.CoachNamePronunciation, FieldLabels.CoachLocation, FieldLabels.CoachGender]
          .map((label) => (
            <CoachInfoFormField
              key={label}
              label={texts[label].label}
              value={basicInfo[label]}
              onChange={(e) => setBasicInfo({ ...basicInfo, [label]: e.target.value })}
              placeholder={texts[label].placeholder}
            />
          ))}
      </FormSection>

      <FormSection>
        <PricingTable pricing={pricing} onPricingChange={handlePricingChange} />
      </FormSection>

      <FormSection>
        {[
          FieldLabels.SpecificRequirements,
          FieldLabels.TrainingProgramme,
          FieldLabels.NutritionPlan,
          FieldLabels.PointOfContact,
          FieldLabels.ResponseTime,
        ].map((label) => (
          <CoachInfoFormField
            key={label}
            label={texts[label].label}
            value={serviceInfo[label]}
            onChange={(e) => setServiceInfo({ ...serviceInfo, [label]: e.target.value })}
            placeholder={texts[label].placeholder}
            multiline={label === FieldLabels.SpecificRequirements}
            rows={label === FieldLabels.SpecificRequirements ? 3 : undefined}
          />
        ))}
      </FormSection>

      <FormSection>
        {[
          FieldLabels.ValuesAndBeliefs,
          FieldLabels.EducationAndQualification,
          FieldLabels.ClientRequirementsAccepts,
          FieldLabels.ClientRequirementsDoesNotAccept,
          FieldLabels.Personas,
        ].map((label) => (
          <CoachInfoFormField
            key={label}
            label={texts[label].label}
            value={professionalInfo[label]}
            onChange={(e) => setProfessionalInfo({ ...professionalInfo, [label]: e.target.value })}
            placeholder={texts[label].placeholder}
            multiline
            rows={4}
          />
        ))}
      </FormSection>

      <FormSection>
        {[
          FieldLabels.ContactEmail,
          FieldLabels.WebsiteURL,
          FieldLabels.Instagram,
          FieldLabels.Tiktok,
          FieldLabels.Youtube,
        ]
          .map((label) => (
            <CoachInfoFormField
              key={label}
              label={texts[label].label}
              value={contactInfo[label]}
              onChange={(e) => setContactInfo({ ...contactInfo, [label]: e.target.value })}
              placeholder={texts[label].placeholder}
            />
          ))}
      </FormSection>

      <SaveButton onClick={onSave} $fitToContent disabled={!isSomeFieldFilled}>
        {texts.save}
      </SaveButton>
    </>

  );
};

CoachInfoForm.propTypes = {
  coachId: PropTypes.string,
  setIsLoading: PropTypes.func.isRequired,
};

CoachInfoForm.defaultProps = {
  coachId: '',
};

export default compose(
  observer,
)(CoachInfoForm);
